//////////////////////////////////////
// Navigation — Mega Menu

(() => {
  'use strict';

  document.addEventListener('DOMContentLoaded', () => {

    //////////////////////////////////////
    // BREAKPOINT
    const mobileMql = window.matchMedia('(max-width: 960px)');


    //////////////////////////////////////
    // DOM REFS
    const overlay      = document.querySelector('.mega-menu-overlay');
    const navButtons   = document.querySelectorAll('.nav-trigger[aria-controls]');
    const searchButton = document.querySelector('.search-icon[aria-controls]');
    const searchPanel  = document.getElementById('search-panel');

    if (!navButtons.length && !searchButton) return;



    //////////////////////////////////////
    // STATE
    let activePanel  = null;
    let activeButton = null;



    //////////////////////////////////////
    // DATA
    const isLowBandwidth = () =>
      document.documentElement.classList.contains('low-bandwidth-mode');


    //////////////////////////////////////
    // HELPERS
    const getFocusable = (container) =>
      [...container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )];


    //////////////////////////////////////
    // ANIMATIONS
    const animateOpen = (panel, onComplete) => {
      if (isLowBandwidth()) { onComplete?.(); return; }

      // Measure panel height while still hidden (visibility:hidden preserves layout)
      const h = panel.offsetHeight;

      // Start: fully hidden above the nav, opacity 0
      gsap.set(panel, { visibility: 'visible', y: -h, opacity: 0 });

      // Sequence: fade in first, then slide down into place
      gsap.timeline({ onComplete })
        .to(panel, { opacity: 1, duration: 0.2, ease: 'power2.out' })
        .to(panel, { y: 0,       duration: 0.4, ease: 'power2.out' }, 0.1);

      // Fade overlay in (skip if already visible from a panel switch)
      if (overlay && !overlay.classList.contains('is-visible')) {
        gsap.fromTo(overlay,
          { opacity: 0 },
          { opacity: 1, duration: 0.3, delay: 0.1, ease: 'power2.out' }
        );
      }
    };

    const animateClose = (panel, onComplete, skipOverlay = false) => {
      if (isLowBandwidth()) {
        if (!skipOverlay && overlay) overlay.classList.remove('is-visible');
        onComplete?.();
        return;
      }

      const h = panel.offsetHeight;

      // Slide up and fade out together
      gsap.timeline({
        onComplete: () => {
          gsap.set(panel, { clearProps: 'opacity,y', visibility: 'hidden' });
          onComplete?.();
        }
      })
      .to(panel, {
          y: -h,
          duration: 0.3,
          ease: 'power2.in'
        });

      // Fade overlay out (skip when switching panels)
      if (overlay && !skipOverlay) {
        gsap.to(overlay, {
          opacity: 0,
          duration: 0.25,
          ease: 'power2.out',
          onComplete: () => {
            overlay.classList.remove('is-visible');
            gsap.set(overlay, { clearProps: 'opacity' });
          }
        });
      }
    };



    //////////////////////////////////////
    // PANEL OPEN
    const openPanel = (button) => {
      const panelId = button.getAttribute('aria-controls');
      const panel   = document.getElementById(panelId);
      if (!panel) return;

      // Close search if open
      if (searchPanel?.classList.contains('is-open')) {
        closeSearch();
      }

      // Close any currently open panel first (no focus return — switching panels)
      if (activePanel && activePanel !== panel) {
        _close();
      }

      activePanel  = panel;
      activeButton = button;

      button.classList.add('is-active');
      button.setAttribute('aria-expanded', 'true');

      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');

      overlay?.classList.add('is-visible');
      document.body.classList.add('mega-menu-open');

      animateOpen(panel);

      // Focus first focusable element in panel
      const focusable = getFocusable(panel);
      focusable[0]?.focus();
    };

    // Internal close — does NOT return focus (used when switching panels)
    const _close = () => {
      if (!activePanel) return;

      const panel  = activePanel;
      const button = activeButton;

      button?.classList.remove('is-active');
      button?.setAttribute('aria-expanded', 'false');

      panel.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('mega-menu-open');

      activePanel  = null;
      activeButton = null;

      animateClose(panel, () => {
        panel.classList.remove('is-open');
      }, true);
    };

    // Public close — returns focus to the triggering button
    const closePanel = () => {
      const returnTo = activeButton;

      const panel  = activePanel;
      const button = activeButton;

      if (!panel) return;

      button?.classList.remove('is-active');
      button?.setAttribute('aria-expanded', 'false');

      panel.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('mega-menu-open');

      activePanel  = null;
      activeButton = null;

      animateClose(panel, () => {
        panel.classList.remove('is-open');
      });

      returnTo?.focus();
    };




    //////////////////////////////////////
    // TAB TRAP
    const trapTab = (e) => {
      if (!activePanel || e.key !== 'Tab') return;

      const focusable = getFocusable(activePanel);
      if (!focusable.length) return;

      const first = focusable[0];
      const last  = focusable[focusable.length - 1];

      if (e.shiftKey) {
        // Shift+Tab from first → wrap to last
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab from last → wrap to first
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };




    //////////////////////////////////////
    // SEARCH PANEL
    const openSearch = () => {
      if (!searchPanel) return;

      // Close any open mega menu first
      if (activePanel) closePanel();

      searchButton.classList.add('is-active');
      searchButton.setAttribute('aria-expanded', 'true');

      searchPanel.classList.add('is-open');
      searchPanel.setAttribute('aria-hidden', 'false');

      overlay?.classList.add('is-visible');
      document.body.classList.add('mega-menu-open');

      animateOpen(searchPanel, () => {
        searchPanel.querySelector('input[type="search"]')?.focus();
      });
    };

    const closeSearch = () => {
      if (!searchPanel || !searchPanel.classList.contains('is-open')) return;

      searchButton?.classList.remove('is-active');
      searchButton?.setAttribute('aria-expanded', 'false');

      searchPanel.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('mega-menu-open');

      animateClose(searchPanel, () => {
        searchPanel.classList.remove('is-open');
      });
    };


    //////////////////////////////////////
    // LIVE SEARCH
    const searchInput   = searchPanel?.querySelector('input[type="search"]');
    const liveResults   = searchPanel?.querySelector('.live-results');
    const searchConfig  = window.searhcSearch || null;
    let debounceTimer   = null;

    if (searchInput && liveResults && searchConfig) {
      searchInput.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        const query = searchInput.value.trim();

        if (query.length < 2) {
          liveResults.innerHTML = '';
          liveResults.classList.remove('has-results');
          return;
        }

        debounceTimer = setTimeout(async () => {
          const url = `${searchConfig.restUrl}?q=${encodeURIComponent(query)}&limit=6`;
          const res = await fetch(url, {
            headers: { 'X-WP-Nonce': searchConfig.nonce }
          });
          const data = await res.json();

          if (!data.results?.length) {
            liveResults.innerHTML = '<div class="no-matches">No results found</div>';
            liveResults.classList.add('has-results');
            return;
          }

          const items = data.results.map(r =>
            `<a href="${r.url}" class="live-result">
              <span class="live-type">${r.type_label}</span>
              <span class="live-title">${r.title}</span>
            </a>`
          ).join('');

          const viewAll = data.total > 6
            ? `<a href="${searchConfig.searchUrl}${encodeURIComponent(query)}" class="view-all">View all ${data.total} results</a>`
            : '';

          liveResults.innerHTML = items + viewAll;
          liveResults.classList.add('has-results');
        }, 300);
      });
    }


    //////////////////////////////////////
    // EVENTS
    // Nav button clicks — toggle open/close
    navButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        if (mobileMql.matches) return;
        e.stopPropagation();

        const panelId = button.getAttribute('aria-controls');
        const panel   = document.getElementById(panelId);

        if (panel?.classList.contains('is-open')) {
          closePanel();
        } else {
          openPanel(button);
        }
      });
    });

    // Link clicks inside a mega-menu panel — close the panel before navigating
    // so the close animation finishes; same-page anchors close immediately
    document.querySelectorAll('.mega-menu-panel a').forEach(link => {
      link.addEventListener('click', (e) => {
        if (mobileMql.matches) return;
        if (!activePanel) return;

        // Modifier-clicks and new-tab links — let the browser handle normally
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
        if (link.target === '_blank') { closePanel(); return; }

        // Same-page link (no real navigation) — close immediately
        const url = new URL(link.href, window.location.origin);
        const isSamePage = url.host === window.location.host && url.pathname === window.location.pathname;
        if (isSamePage) { closePanel(); return; }

        // Otherwise: hold navigation until the close animation finishes,
        // and fade the page (main/footer) so the click feels confirmed
        e.preventDefault();
        if (!isLowBandwidth()) document.body.classList.add('is-navigating');
        closePanel();
        const delay = isLowBandwidth() ? 0 : 720;
        setTimeout(() => { window.location.href = link.href; }, delay);
      });
    });

    // Search icon click — toggle search panel
    searchButton?.addEventListener('click', (e) => {
      if (mobileMql.matches) return;
      e.stopPropagation();
      if (searchPanel?.classList.contains('is-open')) {
        closeSearch();
      } else {
        openSearch();
      }
    });

    // Overlay click → close whichever is open
    overlay?.addEventListener('click', () => {
      if (mobileMql.matches) {
        closeMobileMenu();
        return;
      }
      if (searchPanel?.classList.contains('is-open')) {
        closeSearch();
      } else {
        closePanel();
      }
    });

    // Keyboard events
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        // Mobile: close mobile menu
        if (mobileMql.matches) {
          const mobilePanel = document.getElementById('mobile-nav-panel');
          if (mobilePanel?.classList.contains('is-open')) {
            closeMobileMenu();
            document.querySelector('.mobile-menu-toggle')?.focus();
          }
          return;
        }
        // Desktop: close search or mega menu
        if (searchPanel?.classList.contains('is-open')) {
          closeSearch();
          searchButton?.focus();
          return;
        }
        if (activePanel) {
          closePanel();
          return;
        }
      }
      if (!mobileMql.matches) trapTab(e);
    });




    //////////////////////////////////////
    // INIT — DESKTOP
    // Set initial ARIA state — panels start hidden
    navButtons.forEach(button => {
      button.setAttribute('aria-expanded', 'false');
      const panelId = button.getAttribute('aria-controls');
      const panel   = document.getElementById(panelId);
      panel?.setAttribute('aria-hidden', 'true');
    });



    //////////////////////////////////////
    // MOBILE NAV

    const mobileToggle = document.querySelector('.mobile-menu-toggle');
    const mobilePanel  = document.getElementById('mobile-nav-panel');
    let mobileActive   = false;

    const MOBILE_CHEVRON = '<svg class="preserve-in-low-bandwidth" width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7L13 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';


    //////////////////////////////////////
    // MOBILE ACCORDION TOGGLE
    const toggleMobileAccordion = (header, body) => {
      const section = header.closest('.mobile-nav__section');
      const icon = header.querySelector('.mobile-accordion-icon');
      const isOpen = section.classList.contains('is-open');
      const hasGsap = typeof gsap !== 'undefined';

      if (!hasGsap || isLowBandwidth()) {
        if (isOpen) {
          body.style.height = '0';
          section.classList.remove('is-open');
          header.setAttribute('aria-expanded', 'false');
          body.setAttribute('aria-hidden', 'true');
        } else {
          section.classList.add('is-open');
          body.style.height = 'auto';
          header.setAttribute('aria-expanded', 'true');
          body.setAttribute('aria-hidden', 'false');
        }
        return;
      }

      gsap.killTweensOf(body);
      gsap.killTweensOf(icon);

      if (isOpen) {
        gsap.to(body, {
          height: 0, duration: 0.4, ease: 'power2.inOut',
          onComplete: () => { section.classList.remove('is-open'); body.style.height = '0'; }
        });
        gsap.to(icon, { rotation: 0, duration: 0.4, ease: 'power2.inOut' });
        header.setAttribute('aria-expanded', 'false');
        body.setAttribute('aria-hidden', 'true');
      } else {
        section.classList.add('is-open');
        const naturalHeight = body.scrollHeight;
        gsap.fromTo(body,
          { height: 0 },
          { height: naturalHeight, duration: 0.4, ease: 'power2.inOut',
            onComplete: () => { body.style.height = 'auto'; } }
        );
        gsap.to(icon, { rotation: 180, duration: 0.4, ease: 'power2.inOut' });
        header.setAttribute('aria-expanded', 'true');
        body.setAttribute('aria-hidden', 'false');
      }
    };


    //////////////////////////////////////
    // MOBILE MENU OPEN/CLOSE
    const openMobileMenu = () => {
      if (!mobileToggle || !mobilePanel) return;

      mobileToggle.classList.add('is-active');
      mobileToggle.setAttribute('aria-expanded', 'true');
      mobilePanel.classList.add('is-open');
      mobilePanel.setAttribute('aria-hidden', 'false');
      overlay?.classList.add('is-visible');
      document.body.classList.add('mobile-menu-open');

      if (!isLowBandwidth() && typeof gsap !== 'undefined') {
        gsap.fromTo(mobilePanel,
          { y: -20, opacity: 0, visibility: 'visible' },
          { y: 0, opacity: 1, duration: 0.4, ease: 'power2.out' }
        );
        if (overlay) {
          gsap.fromTo(overlay, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power2.out' });
        }
      }

      const firstFocusable = mobilePanel.querySelector('a, button');
      firstFocusable?.focus();
    };

    const closeMobileMenu = (instant) => {
      if (!mobilePanel?.classList.contains('is-open')) return;

      mobileToggle?.classList.remove('is-active');
      mobileToggle?.setAttribute('aria-expanded', 'false');
      mobilePanel.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('mobile-menu-open');

      const finish = () => {
        mobilePanel.classList.remove('is-open');
        if (typeof gsap !== 'undefined') {
          gsap.set(mobilePanel, { clearProps: 'all' });
          if (overlay) gsap.set(overlay, { clearProps: 'opacity' });
        }
        overlay?.classList.remove('is-visible');
      };

      if (instant || isLowBandwidth() || typeof gsap === 'undefined') {
        finish();
        return;
      }

      gsap.to(mobilePanel, {
        y: -20, opacity: 0, duration: 0.3, ease: 'power2.in',
        onComplete: finish
      });
      if (overlay) {
        gsap.to(overlay, { opacity: 0, duration: 0.25, ease: 'power2.out' });
      }
    };


    //////////////////////////////////////
    // MOBILE INIT / DESTROY
    const initMobile = () => {
      if (mobileActive || !mobileToggle || !mobilePanel) return;

      // Inject chevrons into accordion triggers
      const triggers = mobilePanel.querySelectorAll('.mobile-nav__trigger');
      triggers.forEach((trigger, i) => {
        const iconSpan = document.createElement('span');
        iconSpan.className = 'mobile-accordion-icon';
        iconSpan.setAttribute('aria-hidden', 'true');
        iconSpan.innerHTML = MOBILE_CHEVRON;
        trigger.appendChild(iconSpan);

        const body = trigger.nextElementSibling;
        if (!body) return;

        const panelId = body.id || 'mobile-nav-body-' + i;
        body.id = panelId;
        trigger.setAttribute('aria-controls', panelId);

        const handleClick = () => toggleMobileAccordion(trigger, body);
        const handleKeydown = (e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            handleClick();
          }
        };

        trigger.addEventListener('click', handleClick);
        trigger.addEventListener('keydown', handleKeydown);
        trigger._mobileClick = handleClick;
        trigger._mobileKeydown = handleKeydown;
      });

      // Hamburger toggle
      const handleToggle = () => {
        if (mobilePanel.classList.contains('is-open')) closeMobileMenu();
        else openMobileMenu();
      };
      mobileToggle.addEventListener('click', handleToggle);
      mobileToggle._mobileClick = handleToggle;

      mobileActive = true;
    };

    const destroyMobile = () => {
      if (!mobileActive) return;

      closeMobileMenu(true);

      const triggers = mobilePanel.querySelectorAll('.mobile-nav__trigger');
      triggers.forEach(trigger => {
        if (typeof gsap !== 'undefined') {
          const body = trigger.nextElementSibling;
          const icon = trigger.querySelector('.mobile-accordion-icon');
          if (body) gsap.killTweensOf(body);
          if (icon) gsap.killTweensOf(icon);
        }

        const icon = trigger.querySelector('.mobile-accordion-icon');
        if (icon) icon.remove();

        const body = trigger.nextElementSibling;
        if (body) {
          body.style.height = '';
          body.style.overflow = '';
          body.removeAttribute('aria-hidden');
          if (body.id?.startsWith('mobile-nav-body-')) body.removeAttribute('id');
        }

        trigger.removeAttribute('aria-expanded');
        trigger.removeAttribute('aria-controls');
        trigger.closest('.mobile-nav__section')?.classList.remove('is-open');

        if (trigger._mobileClick) trigger.removeEventListener('click', trigger._mobileClick);
        if (trigger._mobileKeydown) trigger.removeEventListener('keydown', trigger._mobileKeydown);
        delete trigger._mobileClick;
        delete trigger._mobileKeydown;
      });

      if (mobileToggle?._mobileClick) {
        mobileToggle.removeEventListener('click', mobileToggle._mobileClick);
        delete mobileToggle._mobileClick;
      }

      mobileActive = false;
    };


    //////////////////////////////////////
    // MOBILE MATCHMEDIA LIFECYCLE
    const handleMobileBreakpoint = (e) => {
      if (e.matches) initMobile();
      else destroyMobile();
    };
    mobileMql.addEventListener('change', handleMobileBreakpoint);
    handleMobileBreakpoint(mobileMql);


  });
})();
